const CLOUD_BASE = 'https://res.cloudinary.com/do4tedxg6'
const S3_BASE =
  'https://cloudinary-media.s3.us-west-2.amazonaws.com/res/do4tedxg6'

// Only our own image/video upload assets are in the backup bucket and
// anonymously readable — Guesty-hosted and other-cloud URLs are not. Cloudinary
// also caps a fetch source URL at 255 chars and 400s past it, which renders a
// broken image.
//
// Video is included deliberately: instruction-media videos were pruned from
// Cloudinary storage but survive in the backup (#26365), and routing them
// through /video/fetch/ both resurrects them and lets f_auto re-container
// QuickTime .mov sources browsers refuse to decode.
const OWN_ASSET =
  /^https:\/\/res\.cloudinary\.com\/do4tedxg6\/(image|video)\/upload\//
const MAX_FETCH_SOURCE = 255
// A fetch against a key the auto-backup hasn't written yet renders a broken
// image, so assets below this age stay on Cloudinary storage. Backup measured
// as effectively immediate (assets <1h old are already in the bucket); a week
// is a deliberately wide margin, not a measured requirement.
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

// The d_ default Cloudinary serves when the fetch source errors. Cloudinary
// decodes the transformation component once, so a public_id's own percent
// escapes have to survive that decode: %2C would become a comma and split d_
// into bogus parameters (a 400 on an asset that renders fine today), and %2F
// would become a path separator. Those two are re-escaped; everything else is
// passed through untouched — double-encoding %20 makes the default itself 404.
//
// IMAGE ONLY. Cloudinary documents d_ as applying to images, and it measurably
// does not engage on /video/fetch/: a video whose source 404s returns 404 with
// or without d_. It is harmless on video (real videos still deliver 200) but
// buys nothing, so it is omitted rather than implying a fallback that isn't
// there — video call sites carry their own onError fallback instead.
function defaultImage(resourceType, publicId, ext) {
  if (resourceType !== 'image') return ''
  const id = publicId.replace(/\//g, ':').replace(/%(2C|2F)/gi, '%25$1')
  return `d_${id}${ext}`
}

// Cloudinary auto-backup layout:
//   res/<cloud>/<resource_type>/<delivery_type>/<public_id>/<version>.<format>
// raw keeps its extension in the public_id; image and video move it onto the
// version file. Returns the S3 original plus any transformations the URL
// already carried — the upload path chains those after the injected ones, so
// dropping them silently renders e.g. a stored c_fill crop uncropped.
function backupSource(url) {
  const segments = new URL(url).pathname.split('/').filter(Boolean)
  const [, resourceType, deliveryType] = segments
  const versionIndex = segments.findIndex(segment => /^v\d+$/.test(segment))
  const version = segments[versionIndex].slice(1)
  const path = segments.slice(versionIndex + 1).join('/')
  const ext = path.match(/\.\w+$/)?.[0] || ''
  const publicId = ext ? path.slice(0, -ext.length) : path

  return {
    resourceType,
    source: `${S3_BASE}/${resourceType}/${deliveryType}/${publicId}/${version}${ext}`,
    existing: segments.slice(3, versionIndex).join('/'),
    fallback: defaultImage(resourceType, publicId, ext),
  }
}

function backupDeliveryUrl(url, transformations) {
  if (!OWN_ASSET.test(url)) return null

  const version = url.match(/\/v(\d+)\//)
  if (!version) return null
  if (Date.now() - Number(version[1]) * 1000 < ONE_WEEK_MS) return null

  const { resourceType, source, existing, fallback } = backupSource(url)
  if (source.length > MAX_FETCH_SOURCE) return null

  // Image assets deliver via /image/fetch/, video via /video/fetch/ — a video
  // sourced through /image/fetch/ 400s (verified).
  //
  // The d_ default is load-bearing, not belt-and-braces: the backup is keyed by
  // version, but Cloudinary ignores the version in an /upload/ URL (even /v1/
  // resolves), so a stored URL whose asset was later re-uploaded still renders
  // today while its versioned S3 key never existed. Without d_ those render as
  // a broken image; with it Cloudinary falls back to the stored original.
  // Images only — d_ does not engage on /video/fetch/ (see defaultImage).
  return [
    `${CLOUD_BASE}/${resourceType}/fetch`,
    [transformations, fallback].filter(Boolean).join(','),
    existing,
    source,
  ]
    .filter(Boolean)
    .join('/')
}

// Inject Cloudinary delivery transforms (f_auto + q_auto [+ c_limit,w_N]) so the
// browser is served a modern-format, quality-optimized, optionally width-capped
// variant instead of the raw original. Idempotent; non-Cloudinary URLs returned verbatim.
// Image and video assets older than a week are sourced from the S3 backup via
// Cloudinary fetch (same rendered result) so the Cloudinary originals can be
// pruned — and so already-pruned masters keep rendering.
export function cloudinaryDelivery(url, { width } = {}) {
  if (!url) return url
  const secure = url.replace(/^http:\/\/(res\.cloudinary\.com)/, 'https://$1')
  if (!secure.includes('res.cloudinary.com')) return secure
  if (!secure.includes('/upload/')) return secure
  if (/\/upload\/[^/]*f_auto/.test(secure)) return secure
  const parts = ['f_auto', 'q_auto']
  if (width) parts.push(`c_limit,w_${width}`)

  return (
    backupDeliveryUrl(secure, parts.join(',')) ||
    secure.replace('/upload/', `/upload/${parts.join(',')}/`)
  )
}
